import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';

class AdminService {
  constructor() {
    this.api = axios.create({
      baseURL: `${API_BASE_URL}/admin`,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add token to requests and log request data
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('moneysmart-token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        // Log request details for debugging
        if (config.url && config.url.includes('/quizzes/') && config.method === 'patch') {
          console.log('[AdminService] Request interceptor:', {
            url: config.url,
            method: config.method,
            data: config.data,
            hasQuestions: !!config.data?.questions,
            questionsCount: config.data?.questions?.length,
            headers: config.headers
          });
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  // Get analytics
  async getAnalytics() {
    try {
      const response = await this.api.get('/analytics');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get all users
  async getUsers(page = 1, limit = 20, search = '') {
    try {
      const response = await this.api.get('/users', {
        params: { page, limit, search },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get single user
  async getUser(id) {
    try {
      const response = await this.api.get(`/users/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Ban user
  async banUser(id, reason = '') {
    try {
      const response = await this.api.post(`/users/${id}/ban`, { reason });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Unban user
  async unbanUser(id) {
    try {
      const response = await this.api.post(`/users/${id}/unban`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Delete user
  async deleteUser(id) {
    try {
      const response = await this.api.delete(`/users/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Make user admin
  async makeAdmin(id, adminLevel = '1') {
    try {
      const response = await this.api.post(`/users/${id}/make-admin`, { adminLevel });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Update admin level
  async updateAdminLevel(id, adminLevel) {
    try {
      const response = await this.api.put(`/users/${id}/admin-level`, { adminLevel });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Remove admin status
  async removeAdmin(id) {
    try {
      const response = await this.api.post(`/users/${id}/remove-admin`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Admin Notes
  async getUserNotes(userId) {
    try {
      const response = await this.api.get(`/users/${userId}/notes`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async addUserNote(userId, note, type = 'general') {
    try {
      const response = await this.api.post(`/users/${userId}/notes`, { note, type });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateNote(noteId, data) {
    try {
      const response = await this.api.put(`/notes/${noteId}`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteNote(noteId) {
    try {
      const response = await this.api.delete(`/notes/${noteId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Admin Messages
  async getMessages(type = 'all', viewAll = false) {
    try {
      const params = { type };
      if (viewAll) {
        params.viewAll = 'true';
      }
      const response = await this.api.get('/messages', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async sendMessage(receiverId, targetLevels, subject, message, priority = 'normal') {
    try {
      const response = await this.api.post('/messages', { receiverId, targetLevels, subject, message, priority });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async markMessageRead(messageId) {
    try {
      const response = await this.api.put(`/messages/${messageId}/read`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getAdmins() {
    try {
      const response = await this.api.get('/admins');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Account Management
  async addMoneyToAccount(userId, amount, reason = '') {
    try {
      const response = await this.api.post(`/users/${userId}/add-money`, { amount, reason });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getAdminTransactions(userId) {
    try {
      const response = await this.api.get(`/users/${userId}/admin-transactions`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Admin Notifications
  async getNotifications(unreadOnly = false) {
    try {
      const response = await this.api.get('/notifications', {
        params: { unreadOnly: unreadOnly ? 'true' : 'false' }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async markNotificationRead(notificationId) {
    try {
      const response = await this.api.put(`/notifications/${notificationId}/read`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async markAllNotificationsRead() {
    try {
      const response = await this.api.put('/notifications/read-all');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Challenge Management (Level 2+)
  async getChallenges() {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/challenges`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('moneysmart-token')}`,
        },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createChallenge(challengeData) {
    try {
      const response = await axios.post(`${API_BASE_URL}/admin/challenges`, challengeData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('moneysmart-token')}`,
        },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateChallenge(challengeId, challengeData) {
    try {
      const response = await axios.patch(`${API_BASE_URL}/admin/challenges/${challengeId}`, challengeData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('moneysmart-token')}`,
        },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async publishChallenge(challengeId) {
    try {
      const response = await axios.post(`${API_BASE_URL}/admin/challenges/${challengeId}/publish`, {}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('moneysmart-token')}`,
        },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async unpublishChallenge(challengeId) {
    try {
      const response = await axios.post(`${API_BASE_URL}/admin/challenges/${challengeId}/unpublish`, {}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('moneysmart-token')}`,
        },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async activateChallenge(challengeId) {
    try {
      const response = await axios.post(`${API_BASE_URL}/admin/challenges/${challengeId}/activate`, {}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('moneysmart-token')}`,
        },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deactivateChallenge(challengeId) {
    try {
      const response = await axios.post(`${API_BASE_URL}/admin/challenges/${challengeId}/deactivate`, {}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('moneysmart-token')}`,
        },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteChallenge(challengeId) {
    try {
      const response = await axios.delete(`${API_BASE_URL}/admin/challenges/${challengeId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('moneysmart-token')}`,
        },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async forceDeleteChallenge(challengeId) {
    try {
      const response = await axios.delete(`${API_BASE_URL}/admin/challenges/${challengeId}/force`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('moneysmart-token')}`,
        },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getChallengeParticipants(challengeId) {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/challenges/${challengeId}/participants`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('moneysmart-token')}`,
        },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async syncChallengeParticipants(challengeId) {
    try {
      const response = await axios.post(`${API_BASE_URL}/admin/challenges/${challengeId}/sync`, {}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('moneysmart-token')}`,
        },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Lesson Management (Level 2+)
  async getLessons(courseId = null, unitId = null, search = '') {
    try {
      const params = {};
      if (courseId) params.courseId = courseId;
      if (unitId) params.unitId = unitId;
      if (search) params.search = search;
      
      const response = await this.api.get('/lessons', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getLesson(lessonId) {
    try {
      const response = await this.api.get(`/lessons/${lessonId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateLesson(lessonId, lessonData) {
    try {
      const response = await this.api.put(`/lessons/${lessonId}`, lessonData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getCoursesAndUnits() {
    try {
      const response = await this.api.get('/lessons/courses-units');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createCourse(courseData) {
    try {
      const response = await this.api.post('/courses', courseData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createUnit(unitData) {
    try {
      const response = await this.api.post('/units', unitData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createLesson(lessonData) {
    try {
      const response = await this.api.post('/lessons', lessonData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createQuiz(quizData) {
    try {
      const response = await this.api.post('/quizzes', quizData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Quiz Management (Level 2+)
  async getQuizzes(lessonId = null, search = '') {
    try {
      const params = {};
      if (lessonId) params.lessonId = lessonId;
      if (search) params.search = search;
      
      const response = await this.api.get('/quizzes', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getQuiz(quizId) {
    try {
      const response = await this.api.get(`/quizzes/${quizId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateQuiz(quizId, quizData) {
    try {
      // Ensure quizData is a valid object
      if (!quizData || typeof quizData !== 'object') {
        throw new Error('Invalid quiz data provided');
      }

      // Log the request for debugging
      console.log('[AdminService] Updating quiz:', {
        quizId,
        quizData,
        hasQuestions: !!quizData.questions,
        questionsCount: quizData.questions?.length,
        questionsType: Array.isArray(quizData.questions) ? 'array' : typeof quizData.questions,
        stringified: JSON.stringify(quizData)
      });
      
      // Ensure we're sending a proper object with explicit questions field
      const requestData = {
        questions: quizData.questions
      };
      
      // Only include questions if it exists and is an array
      if (requestData.questions === undefined || !Array.isArray(requestData.questions)) {
        throw new Error('Questions must be a valid array');
      }
      
      // Log the exact data being sent
      console.log('[AdminService] Final request data:', {
        quizId,
        requestData,
        questionsLength: requestData.questions.length,
        firstQuestion: requestData.questions[0],
        stringified: JSON.stringify(requestData)
      });
      
      const response = await this.api.patch(`/quizzes/${quizId}`, requestData);
      return response.data;
    } catch (error) {
      console.error('[AdminService] Update quiz error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        requestData: quizData
      });
      throw this.handleError(error);
    }
  }

  // Guest account cleanup (Level 3+)
  async getGuestCount() {
    try {
      const response = await this.api.get('/users/guests/count');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteAllGuests() {
    try {
      const response = await this.api.delete('/users/guests');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Membership tiers (Level 3+)
  async updateUserTier(userId, tier) {
    try {
      const response = await this.api.put(`/users/${userId}/tier`, { tier });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // AI Content Studio (Level 2+)
  async aiStatus() {
    try {
      const response = await this.api.get('/ai/status');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async aiChat(messages) {
    try {
      const response = await this.api.post('/ai/chat', { messages });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async aiGenerateLesson(topic, instructions = '', unitId = null) {
    try {
      const response = await this.api.post('/ai/generate-lesson', { topic, instructions, unitId });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async aiImproveLesson(lessonId, instructions = '') {
    try {
      const response = await this.api.post(`/ai/lessons/${lessonId}/improve`, { instructions });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async aiImproveContent(title, content, instructions = '') {
    try {
      const response = await this.api.post('/ai/improve-content', { title, content, instructions });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async aiGenerateQuiz({ lessonId = null, content = null, title = null, numQuestions = 5 }) {
    try {
      const response = await this.api.post('/ai/generate-quiz', { lessonId, content, title, numQuestions });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  handleError(error) {
    if (error.response) {
      const errorMessage = error.response.data?.error || error.response.data?.message || 'An error occurred';
      const errorDetails = error.response.data?.details || '';
      console.error('[AdminService] Backend error response:', {
        status: error.response.status,
        error: errorMessage,
        details: errorDetails,
        fullResponse: error.response.data
      });
      return new Error(errorDetails ? `${errorMessage}: ${errorDetails}` : errorMessage);
    } else if (error.request) {
      console.error('[AdminService] No response from server:', error.request);
      return new Error('No response from server');
    } else {
      console.error('[AdminService] Request setup error:', error.message);
      return new Error(error.message || 'An error occurred');
    }
  }
}

export default new AdminService();


